import { zodSchemes } from '@approvalmax/utils';
import { PrimaryPurpose, SourceOfFunds } from 'shared/data/walletsKyc/v1';
import { z } from 'zod';

import { messages } from './messages';

const descriptionSchema = zodSchemes.required.textField
    .string()
    .min(15, { message: messages.textLengthError })
    .max(255, { message: messages.textLengthError });

const base = z.object({
    primaryPurpose: zodSchemes.required.select.enum(PrimaryPurpose),
    sourceOfFunds: zodSchemes.required.select.enum(SourceOfFunds),
    collectionsAccount: z
        .enum(['true', 'false'], { required_error: messages.requiredField })
        .transform((value) => value === 'true'),
    askForInitialCapital: z.boolean().nullable(),
    otherSourceOfFunds: zodSchemes.optional.textField.string.nullable(),
    initialCapital: zodSchemes.optional.textField.string.nullable(),
});

const baseSchema = z
    .object({
        accountUsage: base,
    })
    .passthrough();

// Additional validation in superRefine doesn't work until the value passes through baseSchema parsing,
// which causes an issue where conditional field validation errors are not highlighted to the user.
// Therefore, we use zod.preprocess to handle conditional validation
// https://github.com/colinhacks/zod/issues/479#issuecomment-2429834215
export const schema = z.preprocess((input, ctx) => {
    if (typeof input !== 'object' || input === null || !('accountUsage' in input)) {
        return input;
    }

    const accountUsage = input.accountUsage as z.infer<typeof baseSchema>;

    if (accountUsage.askForInitialCapital) {
        const result = descriptionSchema.safeParse(accountUsage.initialCapital);

        if (!result.success) {
            result.error.errors.forEach((err) => {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['accountUsage.initialCapital'],
                    message: err.message,
                });
            });
        }
    }

    if (accountUsage.sourceOfFunds === SourceOfFunds.other) {
        const result = descriptionSchema.safeParse(accountUsage.otherSourceOfFunds);

        if (!result.success) {
            result.error.errors.forEach((err) => {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['accountUsage.otherSourceOfFunds'],
                    message: err.message,
                });
            });
        }
    }

    return input;
}, baseSchema);
